import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { AnimatePresence } from "framer-motion";
import OrangeBySiteStandaloneHeader from "@/components/OrangeBySiteStandaloneHeader";
import OrangeScoreBadge from "@/hq/OrangeScoreBadge";
import { CustomerDetailsModal } from "@/OptimisePage";
import { useApp } from "@/context/AppContext";

const API = process.env.REACT_APP_BACKEND_URL;

const CUR_SYM = { GBP: "£", USD: "$", EUR: "€", AED: "AED " };
const fmt = (minor, cur) =>
  `${CUR_SYM[cur] || cur + " "}${(Number(minor || 0) / 100).toFixed(2)}`;
const SUPPORTED_CURRENCIES = ["GBP", "USD", "EUR", "AED"];

/**
 * Phase 3G.1 (rewired in 3I.3O) — Pitch continuation landing.
 *
 * Consumes `GET /api/optimise/continue/{token}` and drops the recipient
 * straight into the Orange Optimise conversion stage:
 *   - existing Orange Score displayed (no rescan)
 *   - sales proposition + Orange Fix explanation
 *   - canonical pricing from the plan catalogue
 *   - "Choose Orange Optimise" OPENS THE EXISTING CHECKOUT MODAL INLINE,
 *     pre-filled with the customer's known details (never navigates to
 *     the generic /optimise scanner)
 *   - modal submit posts to POST /api/optimise/checkout with the
 *     continuation scan_id and redirects to the /agreement/{token}
 *     review page (existing subscription agreement flow — Phase 3H).
 */
export default function OptimiseContinue() {
  const { token } = useParams();
  const nav = useNavigate();
  const { currency: appCurrency } = useApp();
  const currency = SUPPORTED_CURRENCIES.includes(appCurrency) ? appCurrency : "GBP";

  const [state, setState] = useState({ loading: true });
  const [availability, setAvailability] = useState(null);
  const [modal, setModal] = useState({ open: false, plan: null, cycle: "monthly", error: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/optimise/continue/${encodeURIComponent(token)}`)
      .then((r) => setState({ loading: false, data: r.data }))
      .catch(() => setState({ loading: false, data: { valid: false, fallback_url: "/optimise" } }));
    axios.get(`${API}/api/optimise/checkout/availability`)
      .then((r) => setAvailability(r.data))
      .catch(() => setAvailability({ available: false, reason: "network" }));
  }, [token]);

  if (state.loading) return (
    <div className="min-h-screen bg-[#F6F5F2] flex items-center justify-center">
      <div className="text-[#8A8A8A] text-sm" data-testid="optimise-continue-loading">Loading your scan…</div>
    </div>
  );

  const d = state.data;
  if (!d?.valid) {
    return (
      <div className="min-h-screen bg-[#F6F5F2]">
        <OrangeBySiteStandaloneHeader/>
        <div className="max-w-2xl mx-auto p-8 text-center" data-testid="optimise-continue-invalid">
          <h1 className="text-2xl font-black">Link expired</h1>
          <p className="mt-3 text-[#4A4A4A]">This continuation link is no longer valid. You can still run a fresh Orange Optimise scan from the public page.</p>
          <button
            data-testid="optimise-continue-fallback"
            onClick={() => nav(d?.fallback_url || "/optimise")}
            className="mt-6 px-6 py-3 rounded-full bg-[#FF6A00] text-black text-[11px] font-black tracking-[0.16em] uppercase hover:bg-[#e75f00]"
          >Go to Orange Optimise</button>
        </div>
      </div>
    );
  }

  const scan = d.scan || {};
  const plans = d.plans || [];
  const prefill = d.prefill || null;
  const score = scan.orange_score;

  // Canonical pricing — first Orange Optimise plan in the catalogue.
  const plan = plans[0] || null;
  const monthly = plan?.prices?.[currency]?.monthly;
  const annual  = plan?.prices?.[currency]?.annual;
  const monthsSaved = plan?.annual_savings?.[currency]?.months_saved
    ? Math.floor(plan.annual_savings[currency].months_saved) : 0;

  const openCheckoutModal = (cycle) => {
    if (!plan) return;
    setModal({ open: true, plan, cycle, error: "" });
  };

  const submitCheckout = async (form) => {
    if (!plan) return;
    setSubmitting(true);
    setModal((m) => ({ ...m, error: "" }));
    try {
      const { data } = await axios.post(`${API}/api/optimise/checkout`, {
        plan_key: plan.plan_key,
        currency,
        cycle: modal.cycle,
        scan_id: scan.id,
        ...form,
      });
      if (data?.review_url) { window.location.assign(data.review_url); return; }
      if (data?.approve_url) { window.location.assign(data.approve_url); return; }
      setModal((m) => ({ ...m, error: "Checkout didn't return a next-step URL. Please try again shortly." }));
    } catch (e) {
      const det = e?.response?.data?.detail;
      const msg = typeof det === "string" ? det
        : det?.customer_message || det?.reason
        || (e?.response?.status === 503
              ? "This payment provider is being enabled. Please try another supported currency."
              : "Something went wrong preparing checkout. Please try again shortly.");
      setModal((m) => ({ ...m, error: msg }));
    } finally { setSubmitting(false); }
  };

  const scoreLine = typeof score === "number"
    ? `Your website scored ${score} today. Orange Optimise keeps watching it so you'll know when something changes.`
    : `Orange Optimise keeps watching your website so you'll know when something changes.`;

  return (
    <div className="min-h-screen bg-[#F6F5F2]" data-testid="optimise-continue">
      <OrangeBySiteStandaloneHeader/>
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8 md:py-14">
        {/* Recap — existing scan reused */}
        <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-6 items-center bg-white rounded-2xl p-6 border border-[#EAEAEA] shadow-sm">
          <div className="flex items-center justify-center" data-testid="optimise-continue-score">
            <OrangeScoreBadge score={score} size="lg"/>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">We already scanned your website</div>
            <h1 className="mt-1 font-black text-3xl md:text-4xl tracking-[-0.02em]" data-testid="optimise-continue-headline">
              Keep your website healthy. 🍊
            </h1>
            <p className="mt-3 text-[#4A4A4A] text-[15px]" data-testid="optimise-continue-score-line">{scoreLine}</p>
            {scan.hero_subhead && (
              <p className="mt-2 text-[#4A4A4A] text-sm" data-testid="optimise-continue-subhead">{scan.hero_subhead}</p>
            )}
          </div>
        </div>

        {/* Sales proposition */}
        <section className="mt-8 bg-white rounded-2xl p-6 md:p-8 border border-[#EAEAEA]" data-testid="optimise-continue-benefits">
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">What you get with Orange Optimise</div>
          <h2 className="mt-1 font-black text-2xl md:text-3xl tracking-[-0.02em]">
            {monthly ? `For ${fmt(monthly, currency)}/month:` : "Continuous website health monitoring:"}
          </h2>
          <ul className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2 text-[15px]">
            {[
              "Continuous website health monitoring",
              "Orange Score tracking over time",
              "Detection of new and recurring website issues",
              "Performance monitoring",
              "Mobile monitoring",
              "Technical health monitoring",
              "Security-health monitoring based on externally detectable signals",
              "Alerts when meaningful changes are detected",
              "Issue history and clear recommendations",
            ].map((b) => (
              <li key={b} className="flex gap-2 text-black/85">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#FF6A00] shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Orange Fix explanation */}
        <section className="mt-6 bg-[#FFF7EE] border border-[#FCE1C7] rounded-2xl p-6 md:p-7" data-testid="optimise-continue-fix">
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Found something that needs fixing?</div>
          <p className="mt-2 text-[15px] leading-relaxed text-[#4A4A4A]">
            Orange Optimise monitors and diagnoses your website. If you'd rather OrangeBySite fix an identified problem for you, you can request an <strong>Orange Fix</strong> quote. Orange Fix work is quoted separately — it is not included in the Orange Optimise subscription and you are never automatically charged for remediation work.
          </p>
        </section>

        {/* Plan CTA — opens the existing checkout modal directly */}
        <section className="mt-8 bg-white rounded-2xl p-6 md:p-8 border-2 border-[#FF6A00]" data-testid="optimise-continue-plan-card">
          <div className="flex flex-wrap items-baseline justify-between gap-4">
            <div>
              <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#FF6A00]">
                {plan?.name || "Orange Optimise"}
              </div>
              <div className="mt-2 flex items-baseline gap-2">
                <div className="text-4xl font-black" data-testid="optimise-continue-price-monthly">
                  {monthly ? fmt(monthly, currency) : "—"}
                </div>
                <div className="text-sm text-[#8A8A8A]">/ month</div>
              </div>
              {annual && (
                <div className="mt-1 text-sm text-[#4A4A4A]" data-testid="optimise-continue-price-annual">
                  or <strong>{fmt(annual, currency)}</strong> / year
                  {monthsSaved > 0 && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full bg-[#FF6A00] text-black text-[10px] font-black tracking-[0.16em] uppercase">
                      SAVE {monthsSaved} MONTH{monthsSaved > 1 ? "S" : ""}
                    </span>
                  )}
                </div>
              )}
              <p className="mt-3 text-[12px] text-black/60 max-w-lg">Cancel any time. Existing scan reused — no need to re-scan.</p>
            </div>
            <div className="flex flex-col gap-2 min-w-[180px]">
              <button
                type="button"
                data-testid={`optimise-continue-buy-${plan?.plan_key || "optimise"}-monthly`}
                onClick={() => openCheckoutModal("monthly")}
                disabled={!plan || !monthly}
                className="px-6 py-3.5 rounded-full bg-black text-white font-black text-[12px] tracking-[0.2em] uppercase hover:bg-[#1A1A1A] disabled:opacity-50"
              >
                Choose Orange Optimise
              </button>
              {annual && (
                <button
                  type="button"
                  data-testid={`optimise-continue-buy-${plan?.plan_key || "optimise"}-annual`}
                  onClick={() => openCheckoutModal("annual")}
                  className="px-6 py-3.5 rounded-full bg-white border-2 border-black text-black font-black text-[12px] tracking-[0.2em] uppercase hover:bg-black hover:text-white"
                >
                  Choose annual
                </button>
              )}
            </div>
          </div>
        </section>

        <p className="mt-6 text-[12px] text-[#8A8A8A]" data-testid="optimise-continue-footnote">
          Orange Optimise monitors and diagnoses your website. Orange Fix (remediation work) is quoted separately.
        </p>
      </div>

      <AnimatePresence>
        {modal.open && (
          <CustomerDetailsModal
            open={modal.open}
            plan={modal.plan}
            currency={currency}
            cycle={modal.cycle}
            scan={{ id: scan.id, submitted_url: scan.submitted_url || prefill?.website_url }}
            availability={availability}
            prefill={prefill}
            onClose={() => setModal((m) => ({ ...m, open: false }))}
            onSubmit={submitCheckout}
            submitting={submitting}
            error={modal.error}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
